import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Table,
  Button,
  Icon,
  Modal,
  Label,
  Grid,
  Segment,
  Header as SemanticHeader,
  Input,
  Dropdown,
  Menu,
  Dimmer,
  Loader,
  Popup,
  DropdownItemProps
} from 'semantic-ui-react';
import styled from 'styled-components';
import { iRootState } from '../../store';
import {
  getLenderInsurances,
  getLenderInsuranceById,
  cancelLenderInsurance,
  rejectLenderInsurance,
  getLenderInsuranceSourceContext,
  getLenderInsuranceBatchesByInsuranceId,
  LenderInsurance,
  InsuranceBatchInfo
} from '../../services/lender-insurance';
import { dangerNotification, successNotification } from '../../services/notification';
import EstateUnitFilter from '../../components/EstateUnitFilter';
import CreateLenderInsuranceModal from './components/CreateLenderInsuranceModal';
import TablePaginationMenu from '../../components/TablePaginationMenu';
import { maskCpf, maskCnpj, maskMoney } from '../../services/masks';
import moment from 'moment';
import LenderInsuranceGeneralConditionModal from './components/LenderInsuranceGeneralConditionModal';
import { genderOptions, maritalStatusOptions } from '../bond-insurance-budget-form/types';
import { UserRolesEnum } from '../../enums/user-roles.enum';

const Container = styled.div`
  padding: 20px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  align-items: center;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${props =>
    props.status === 'ACTIVE'
      ? '#2e7d32'
      : props.status === 'REJECTED'
        ? '#c62828'
        : '#8e8e8e'};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props =>
      props.status === 'ACTIVE'
        ? '#4caf50'
        : props.status === 'REJECTED'
          ? '#e53935'
          : '#bdbdbd'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;

  h3 {
    color: #333;
    margin-bottom: 10px;
  }
`;

const ClickableRow = styled(Table.Row)`
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f5f5f5 !important;
  }
`;

const Header = styled(Table.Header)`
  background-color: #e1e8ed;
`;

const TableWrapper = styled.div`
  font-size: 0.85em;
  font-weight: bold;

  .ui.table {
    border: none;
    text-align: center;
    color: #364445;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 20px;
`;

const DetailTitle = styled.h4`
  color: #59249b;
  border-bottom: 2px solid #59249b;
  padding-bottom: 8px;
  margin-bottom: 16px;
`;

const DetailItem = styled.div`
  margin-bottom: 12px;
`;

const DetailLabel = styled.span`
  color: #666;
  font-size: 12px;
  display: block;
  margin-bottom: 2px;
`;

const DetailValue = styled.span`
  color: #333;
  font-size: 14px;
  font-weight: 500;
`;

const SourceText = styled.span<{ sourceColor: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #555;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => props.sourceColor};
  }
`;

const NotFoundWrapper = styled.div`
  width: 100%;
  text-align: center;
  color: #364445;
  font-size: 1.2em;
`;

const mapState = (state: iRootState) => ({
  user: state.user,
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

type Props = ReturnType<typeof mapState>;
const LenderInsuranceList: React.FC<Props> = ({ user, availableBrokersAndEstates }) => {
  const [insurances, setInsurances] = useState<LenderInsurance[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState<LenderInsurance | null>(
    null
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [insuranceBatches, setInsuranceBatches] = useState<InsuranceBatchInfo[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [openConditions, setOpenConditions] = useState<boolean>(false);
  const history = useHistory();
  const location = useLocation();

  // Pagination
  const [activePage, setActivePage] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const ITEMS_PER_PAGE = 10;

  // Filters state
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');
  const [estateUnitIdFilter, setEstateUnitIdFilter] = useState('');

  // Check if user is admin (no estate linked)
  const isAdmin = !user.estate?.id;
  const isEstateQuotationUser = user.role === UserRolesEnum.estateQuotation;

  const fetchData = async (page = activePage) => {
    try {
      setLoading(true);
      // For admins, pass empty string to get all records, else pass estateId
      const estateId = user.estate?.id || '';

      const result = await getLenderInsurances({
        estateId,
        estateUnitId: estateUnitIdFilter || undefined,
        search: searchText,
        status: statusFilter,
        sourceType: sourceTypeFilter,
        page,
        limit: ITEMS_PER_PAGE
      });
      setInsurances(result.data);
      setDataCount(result.count);
    } catch (error) {
      dangerNotification('Erro', 'Erro ao carregar seguros prestamistas');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setSourceTypeFilter('');
    setActivePage(1);
    // fetchData will be called by useEffect when dependencies change if we add them,
    // or we can call it explicitly.
    // Let's rely on explicit call for "Clear" button to ensure clean slate.
    // Wait, state update is async. Better to having useEffect listen to filters?
    // No, usually filters are applied on "Search" button or debounce.
    // Let's make "Filtrar" button trigger.
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.estate?.id]); // Only initial load or user change. Filters triggering manually or we can add them here.

  // Handle navigation from other pages (capitalization, fire insurance, bond insurance, carteira)
  useEffect(() => {
    const state = location.state as { lenderInsuranceId?: string } | undefined;
    const idFromQuery = new URLSearchParams(location.search).get('id');
    const targetId = state?.lenderInsuranceId || idFromQuery;

    if (!targetId) return;

    const openInsurance = async (insurance: LenderInsurance) => {
      setSelectedInsurance(insurance);
      setDetailModalOpen(true);
      setLoadingBatches(true);
      setInsuranceBatches([]);
      try {
        const batches = await getLenderInsuranceBatchesByInsuranceId(insurance.id);
        setInsuranceBatches(batches);
      } catch (error) {
        console.error('Error loading batches:', error);
      } finally {
        setLoadingBatches(false);
      }
    };

    const resolveAndOpen = async () => {
      const fromList = insurances.find(i => i.id === targetId);
      if (fromList) {
        await openInsurance(fromList);
      } else {
        try {
          const insurance = await getLenderInsuranceById(targetId);
          await openInsurance(insurance);
        } catch (error) {
          console.error('Error loading lender insurance:', error);
        }
      }

      if (state?.lenderInsuranceId) {
        history.replace({ ...location, state: undefined });
      }
      if (idFromQuery) {
        history.replace({ pathname: location.pathname, search: '' });
      }
    };

    resolveAndOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search]);

  // Let's add explicit Search trigger
  const handleSearch = () => {
    setActivePage(1);
    fetchData(1);
  };

  const handleCancel = async () => {
    if (!selectedId) return;

    try {
      setCancelling(true);
      await cancelLenderInsurance(selectedId);
      successNotification('Sucesso', 'Seguro cancelado com sucesso');
      setCancelModalOpen(false);
      setSelectedId(null);
      fetchData();
    } catch (error) {
      dangerNotification('Erro', 'Erro ao cancelar seguro');
    } finally {
      setCancelling(false);
    }
  };

  const handleReject = async () => {
    if (!selectedId) return;

    try {
      setRejecting(true);
      await rejectLenderInsurance(selectedId);
      successNotification('Sucesso', 'Seguro marcado como recusado');
      setRejectModalOpen(false);
      setSelectedId(null);
      fetchData();
    } catch (error) {
      dangerNotification('Erro', 'Erro ao recusar seguro');
    } finally {
      setRejecting(false);
    }
  };

  const handleRowClick = async (insurance: LenderInsurance) => {
    setSelectedInsurance(insurance);
    setDetailModalOpen(true);
    setLoadingBatches(true);
    setInsuranceBatches([]);
    try {
      const batches = await getLenderInsuranceBatchesByInsuranceId(insurance.id);
      setInsuranceBatches(batches);
    } catch (error) {
      console.error('Error loading batches:', error);
    } finally {
      setLoadingBatches(false);
    }
  };

  const getBatchStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '#f2711c';
      case 'BILLED':
        return '#2185d0';
      case 'PAID':
        return '#21ba45';
      case 'OVERDUE':
        return '#db2828';
      case 'CANCELLED':
        return '#767676';
      default:
        return '#767676';
    }
  };

  const getBatchStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendente';
      case 'BILLED':
        return 'Faturado';
      case 'PAID':
        return 'Pago';
      case 'OVERDUE':
        return 'Vencido';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const openCancelModal = (e: React.MouseEvent, insurance: LenderInsurance) => {
    e.stopPropagation();
    setSelectedId(insurance.id);
    setCancelModalOpen(true);
  };

  const openRejectModal = (e: React.MouseEvent, insurance: LenderInsurance) => {
    e.stopPropagation();
    setSelectedId(insurance.id);
    setRejectModalOpen(true);
  };

  const formatDocument = (doc: string) => {
    if (!doc) return '-';
    const cleaned = doc.replace(/\D/g, '');
    return cleaned.length > 11 ? maskCnpj(cleaned) : maskCpf(cleaned);
  };

  const getOptionLabel = (
    value: string | undefined,
    options: DropdownItemProps[]
  ): React.ReactNode => {
    if (!value) return '-';
    const option = options.find(item => item.value === value);
    return option?.text ?? value;
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Ativo';
      case 'REJECTED':
        return 'Recusado';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const getSourceTypeInfo = (sourceType: string) => {
    const map: Record<string, { text: string; dotColor: string }> = {
      FIRE_INSURANCE: { text: 'Incêndio', dotColor: '#e65100' },
      BOND_INSURANCE: { text: 'Fiança', dotColor: '#1565c0' },
      CAPITALIZATION: { text: 'Capitalização', dotColor: '#2e7d32' },
      STANDALONE: { text: 'Avulso', dotColor: '#757575' }
    };
    return map[sourceType] || { text: sourceType, dotColor: '#757575' };
  };

  const handleViewSource = async (insurance: LenderInsurance) => {
    if (!insurance.sourceId) return;

    try {
      const context = await getLenderInsuranceSourceContext(insurance.id);

      if (context && context.route) {
        const currentPath = location.pathname;
        const basePath = currentPath.substring(0, currentPath.lastIndexOf('/'));
        const base = basePath || '';

        const targetPath = `${base}${context.route}`;
        const params = context.queryParams
          ? `?${new URLSearchParams(context.queryParams).toString()}`
          : '';

        history.push(`${targetPath}${params}`, context.stateParams);
      }
    } catch (error) {
      console.error('Error fetching source context', error);
    }
  };

  const toggleOpenConditions = useCallback(() => {
    setOpenConditions(true);
  }, []);

  const toggleCloseConditions = useCallback(() => {
    setOpenConditions(false);
  }, []);

  return (
    <Container>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}
      >
        {!isEstateQuotationUser &&
          (user.estate?.lenderInsuranceEnabled || !user.estate?.id) && (
            <Button
              style={{ backgroundColor: '#59249B', color: 'white' }}
              onClick={() => setCreateModalOpen(true)}
            >
              <Icon name="plus" /> Novo Seguro
            </Button>
          )}
        <LenderInsuranceGeneralConditionModal
          openModal={openConditions}
          toggleOpen={toggleOpenConditions}
          toggleClose={toggleCloseConditions}
        />
      </div>

      <FiltersContainer>
        {user.estate?.id && (
          <div style={{ minWidth: 220 }}>
            <EstateUnitFilter
              estateId={String(user.estate.id)}
              value={estateUnitIdFilter}
              onChange={setEstateUnitIdFilter}
            />
          </div>
        )}
        <Input
          icon="search"
          placeholder="Buscar inquilino, documento..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onKeyPress={(e: any) => e.key === 'Enter' && handleSearch()}
        />
        <Dropdown
          selection
          placeholder="Status"
          options={[
            { key: 'all', text: 'Todos Status', value: '' },
            { key: 'ACTIVE', text: 'Ativo', value: 'ACTIVE' },
            { key: 'CANCELLED', text: 'Cancelado', value: 'CANCELLED' },
            { key: 'REJECTED', text: 'Recusado', value: 'REJECTED' }
          ]}
          value={statusFilter}
          onChange={(e, { value }) => setStatusFilter(value as string)}
        />
        <Dropdown
          selection
          placeholder="Origem"
          options={[
            { key: 'all', text: 'Todas Origens', value: '' },
            { key: 'BOND_INSURANCE', text: 'Seguro Fiança', value: 'BOND_INSURANCE' },
            { key: 'FIRE_INSURANCE', text: 'Seguro Incêndio', value: 'FIRE_INSURANCE' },
            { key: 'CAPITALIZATION', text: 'Capitalização', value: 'CAPITALIZATION' },
            { key: 'STANDALONE', text: 'Avulso', value: 'STANDALONE' }
          ]}
          value={sourceTypeFilter}
          onChange={(e, { value }) => setSourceTypeFilter(value as string)}
        />
        <Button primary onClick={handleSearch}>
          Filtrar
        </Button>
        <Button onClick={handleClearFilters}>Limpar</Button>
      </FiltersContainer>

      <Dimmer.Dimmable
        as={Segment}
        dimmed={loading}
        basic
        style={{ padding: 0, border: 'none', boxShadow: 'none' }}
      >
        <Dimmer active={loading} inverted>
          <Loader />
        </Dimmer>

        {insurances.length === 0 && !loading ? (
          <NotFoundWrapper>
            <p>Nenhum seguro prestamista encontrado</p>
          </NotFoundWrapper>
        ) : (
          <TableWrapper>
            <Table tablet stackable>
              <Header>
                <Table.Row>
                  {isAdmin && <Table.HeaderCell>IMOBILIÁRIA</Table.HeaderCell>}
                  <Table.HeaderCell>LOCATÁRIO</Table.HeaderCell>
                  <Table.HeaderCell>CPF/CNPJ</Table.HeaderCell>
                  <Table.HeaderCell>PROPRIETÁRIO</Table.HeaderCell>
                  <Table.HeaderCell>ORIGEM</Table.HeaderCell>
                  <Table.HeaderCell>VALOR</Table.HeaderCell>
                  <Table.HeaderCell>STATUS</Table.HeaderCell>
                  <Table.HeaderCell>CRIADO EM</Table.HeaderCell>
                  {!isEstateQuotationUser && (
                    <Table.HeaderCell>AÇÕES</Table.HeaderCell>
                  )}
                </Table.Row>
              </Header>

              <Table.Body>
                {insurances.map(insurance => {
                  const source = getSourceTypeInfo(insurance.sourceType);
                  return (
                    <ClickableRow
                      key={insurance.id}
                      onClick={() => handleRowClick(insurance)}
                    >
                      {isAdmin && (
                        <Table.Cell>{insurance.estate?.name || '-'}</Table.Cell>
                      )}
                      <Table.Cell>{insurance.tenantName}</Table.Cell>
                      <Table.Cell>
                        <span style={{ fontSize: '12px', color: '#808080' }}>
                          {formatDocument(insurance.tenantDocument)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>{insurance.ownerName}</Table.Cell>
                      <Table.Cell>
                        <SourceText sourceColor={source.dotColor}>
                          {source.text}
                        </SourceText>
                      </Table.Cell>
                      <Table.Cell>
                        {insurance.insuranceValue
                          ? `R$ ${maskMoney(String(insurance.insuranceValue), true)}`
                          : '-'}
                      </Table.Cell>
                      <Table.Cell>
                        <StatusBadge status={insurance.status}>
                          {getStatusText(insurance.status)}
                        </StatusBadge>
                      </Table.Cell>
                      <Table.Cell>
                        {moment(insurance.createdAt).format('DD/MM/YYYY')}
                      </Table.Cell>
                      {!isEstateQuotationUser && (
                        <Table.Cell>
                          {insurance.status === 'ACTIVE' && (
                            <div
                              style={{
                                display: 'flex',
                                gap: 4,
                                justifyContent: 'center',
                                flexWrap: 'wrap'
                              }}
                              onClick={e => e.stopPropagation()}
                            >
                              <Popup
                                content="Cancelar seguro"
                                trigger={
                                  <Button
                                    icon
                                    basic
                                    size="small"
                                    onClick={e => openCancelModal(e, insurance)}
                                  >
                                    <Icon name="cancel" color="grey" />
                                  </Button>
                                }
                              />
                              {isAdmin && (
                                <Popup
                                  content="Recusar seguro"
                                  trigger={
                                    <Button
                                      icon
                                      basic
                                      size="small"
                                      color="red"
                                      onClick={e => openRejectModal(e, insurance)}
                                    >
                                      <Icon name="ban" />
                                    </Button>
                                  }
                                />
                              )}
                            </div>
                          )}
                        </Table.Cell>
                      )}
                    </ClickableRow>
                  );
                })}
              </Table.Body>
              <Table.Footer>
                <Table.Row>
                  <Table.HeaderCell
                    colSpan={(isAdmin ? 10 : 9) - (isEstateQuotationUser ? 1 : 0)}
                  >
                    <TablePaginationMenu
                      currentPage={activePage}
                      dataCount={dataCount}
                      setCurrentPage={page => {
                        setActivePage(page);
                        fetchData(page);
                      }}
                    />
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Footer>
            </Table>
          </TableWrapper>
        )}
      </Dimmer.Dimmable>

      {/* Detail Modal */}
      <Modal
        size="small"
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
      >
        <Modal.Header>
          <Icon name="shield" /> Detalhes do Seguro Prestamista
          {selectedInsurance && (
            <StatusBadge status={selectedInsurance.status} style={{ marginLeft: 16 }}>
              {getStatusText(selectedInsurance.status)}
            </StatusBadge>
          )}
        </Modal.Header>
        <Modal.Content>
          {selectedInsurance && (
            <Grid>
              {/* Estate section for admins */}
              {isAdmin && selectedInsurance.estate && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    <DetailSection>
                      <DetailTitle>
                        <Icon name="building outline" /> Imobiliária
                      </DetailTitle>
                      <Grid columns={3}>
                        <Grid.Column>
                          <DetailItem>
                            <DetailLabel>Nome</DetailLabel>
                            <DetailValue style={{ fontWeight: 'bold' }}>
                              {selectedInsurance.estate.name || '-'}
                            </DetailValue>
                          </DetailItem>
                        </Grid.Column>
                        <Grid.Column>
                          <DetailItem>
                            <DetailLabel>CNPJ</DetailLabel>
                            <DetailValue>
                              {selectedInsurance.estate.cnpj
                                ? formatDocument(selectedInsurance.estate.cnpj)
                                : '-'}
                            </DetailValue>
                          </DetailItem>
                        </Grid.Column>
                        <Grid.Column>
                          <DetailItem>
                            <DetailLabel>E-mail</DetailLabel>
                            <DetailValue>
                              {selectedInsurance.estate.email || '-'}
                            </DetailValue>
                          </DetailItem>
                        </Grid.Column>
                      </Grid>
                    </DetailSection>
                  </Grid.Column>
                </Grid.Row>
              )}

              <Grid.Row>
                <Grid.Column width={8}>
                  <DetailSection>
                    <DetailTitle>
                      <Icon name="user" /> Inquilino
                    </DetailTitle>
                    <DetailItem>
                      <DetailLabel>Nome</DetailLabel>
                      <DetailValue>{selectedInsurance.tenantName || '-'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>CPF/CNPJ</DetailLabel>
                      <DetailValue>
                        {formatDocument(selectedInsurance.tenantDocument)}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>E-mail</DetailLabel>
                      <DetailValue>{selectedInsurance.tenantEmail || '-'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Telefone</DetailLabel>
                      <DetailValue>{selectedInsurance.tenantPhone || '-'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Data de Nascimento</DetailLabel>
                      <DetailValue>
                        {selectedInsurance.tenantBirthDate
                          ? new Date(selectedInsurance.tenantBirthDate).toLocaleDateString('pt-BR')
                          : '-'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Sexo</DetailLabel>
                      <DetailValue>
                        {getOptionLabel(selectedInsurance.tenantGender, genderOptions)}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Estado Civil</DetailLabel>
                      <DetailValue>
                        {getOptionLabel(selectedInsurance.tenantMaritalStatus, maritalStatusOptions)}
                      </DetailValue>
                    </DetailItem>
                  </DetailSection>
                </Grid.Column>

                <Grid.Column width={8}>
                  <DetailSection>
                    <DetailTitle>
                      <Icon name="building" /> Proprietário
                    </DetailTitle>
                    <DetailItem>
                      <DetailLabel>Nome</DetailLabel>
                      <DetailValue>{selectedInsurance.ownerName || '-'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>CPF/CNPJ</DetailLabel>
                      <DetailValue>
                        {formatDocument(selectedInsurance.ownerDocument)}
                      </DetailValue>
                    </DetailItem>
                  </DetailSection>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16}>
                  <DetailSection>
                    <DetailTitle>
                      <Icon name="home" /> Imóvel
                    </DetailTitle>
                    <DetailItem>
                      <DetailLabel>Endereço</DetailLabel>
                      <DetailValue>
                        {selectedInsurance.propertyAddress || '-'}
                      </DetailValue>
                    </DetailItem>
                    <Grid columns={3}>
                      <Grid.Column>
                        <DetailItem>
                          <DetailLabel>CEP</DetailLabel>
                          <DetailValue>
                            {selectedInsurance.propertyZipcode || '-'}
                          </DetailValue>
                        </DetailItem>
                      </Grid.Column>
                      <Grid.Column>
                        <DetailItem>
                          <DetailLabel>Cidade</DetailLabel>
                          <DetailValue>
                            {selectedInsurance.propertyCity || '-'}
                          </DetailValue>
                        </DetailItem>
                      </Grid.Column>
                      <Grid.Column>
                        <DetailItem>
                          <DetailLabel>Estado</DetailLabel>
                          <DetailValue>
                            {selectedInsurance.propertyState || '-'}
                          </DetailValue>
                        </DetailItem>
                      </Grid.Column>
                    </Grid>
                  </DetailSection>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={8}>
                  <DetailSection>
                    <DetailTitle>
                      <Icon name="dollar" /> Valores
                    </DetailTitle>
                    <DetailItem>
                      <DetailLabel>Valor do Aluguel</DetailLabel>
                      <DetailValue style={{ color: '#59249B', fontWeight: 'bold' }}>
                        {maskMoney(String(selectedInsurance.rentValue), true)}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Valor do Seguro</DetailLabel>
                      <DetailValue style={{ color: '#59249B', fontWeight: 'bold' }}>
                        {selectedInsurance.insuranceValue
                          ? `R$ ${maskMoney(
                              String(selectedInsurance.insuranceValue),
                              true
                            )}`
                          : '-'}
                      </DetailValue>
                    </DetailItem>
                  </DetailSection>
                </Grid.Column>

                <Grid.Column width={8}>
                  <DetailSection>
                    <DetailTitle>
                      <Icon name="calendar" /> Datas e Origem
                    </DetailTitle>
                    <DetailItem>
                      <DetailLabel>Data de Contratação</DetailLabel>
                      <DetailValue>
                        {moment(selectedInsurance.createdAt).format('DD/MM/YYYY HH:mm')}
                      </DetailValue>
                    </DetailItem>
                    {selectedInsurance.cancelledAt && (
                      <DetailItem>
                        <DetailLabel>Data de Cancelamento</DetailLabel>
                        <DetailValue>
                          {selectedInsurance?.cancelledAt
                            ? moment(selectedInsurance.cancelledAt).format(
                                'DD/MM/YYYY HH:mm'
                              )
                            : '-'}
                        </DetailValue>
                      </DetailItem>
                    )}
                    {selectedInsurance.rejectedAt && (
                      <DetailItem>
                        <DetailLabel>Data de Recusa</DetailLabel>
                        <DetailValue>
                          {moment(selectedInsurance.rejectedAt).format(
                            'DD/MM/YYYY HH:mm'
                          )}
                        </DetailValue>
                      </DetailItem>
                    )}
                    <DetailItem
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div>
                        <DetailLabel>Origem</DetailLabel>
                        <DetailValue>
                          <SourceText
                            sourceColor={
                              getSourceTypeInfo(selectedInsurance.sourceType).dotColor
                            }
                          >
                            {getSourceTypeInfo(selectedInsurance.sourceType).text}
                          </SourceText>
                        </DetailValue>
                      </div>
                      {selectedInsurance.sourceId &&
                        selectedInsurance.sourceType !== 'STANDALONE' && (
                          <Button
                            icon
                            labelPosition="right"
                            size="tiny"
                            onClick={() => handleViewSource(selectedInsurance)}
                          >
                            Ver Seguro
                            <Icon name="arrow right" />
                          </Button>
                        )}
                    </DetailItem>
                  </DetailSection>
                </Grid.Column>
              </Grid.Row>

              {/* Batches Section */}
              <Grid.Row>
                <Grid.Column width={16}>
                  <DetailSection>
                    <DetailTitle>
                      <Icon name="archive" /> Lotes Emitidos
                    </DetailTitle>
                    {loadingBatches ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Loader active inline size="small" /> Carregando lotes...
                      </div>
                    ) : insuranceBatches.length === 0 ? (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '15px',
                          color: '#999',
                          fontSize: '13px'
                        }}
                      >
                        <Icon name="info circle" /> Este seguro ainda não foi incluído em
                        nenhum lote.
                      </div>
                    ) : (
                      <Table compact size="small" basic="very">
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>Referência</Table.HeaderCell>
                            <Table.HeaderCell>Status</Table.HeaderCell>
                            <Table.HeaderCell>Valor do Item</Table.HeaderCell>
                            <Table.HeaderCell></Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {insuranceBatches.map(batch => (
                            <Table.Row key={batch.id}>
                              <Table.Cell>
                                <strong>
                                  {String(batch.referenceMonth).padStart(2, '0')}/
                                  {batch.referenceYear}
                                </strong>
                              </Table.Cell>
                              <Table.Cell>
                                <Label
                                  size="mini"
                                  style={{
                                    backgroundColor: getBatchStatusColor(batch.status),
                                    color: 'white'
                                  }}
                                >
                                  {getBatchStatusLabel(batch.status)}
                                </Label>
                              </Table.Cell>
                              <Table.Cell>
                                R$ {maskMoney(String(batch.itemValue), true)}
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                {!isEstateQuotationUser && (
                                  <Button
                                    icon
                                    labelPosition="right"
                                    size="mini"
                                    basic
                                    color="purple"
                                    onClick={() => {
                                      setDetailModalOpen(false);
                                      history.push('/admin/lender-insurance-batches', {
                                        filterEstateId:
                                          selectedInsurance?.estateId || '',
                                        filterMonth: batch.referenceMonth,
                                        filterYear: batch.referenceYear
                                      });
                                    }}
                                  >
                                    Ver Lote
                                    <Icon name="arrow right" />
                                  </Button>
                                )}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    )}
                  </DetailSection>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDetailModalOpen(false)}>
            <Icon name="close" /> Fechar
          </Button>
          {!isEstateQuotationUser && selectedInsurance?.status === 'ACTIVE' && (
            <>
              <Button
                color="red"
                onClick={() => {
                  setDetailModalOpen(false);
                  setSelectedId(selectedInsurance.id);
                  setCancelModalOpen(true);
                }}
              >
                <Icon name="cancel" /> Cancelar Seguro
              </Button>
              {isAdmin && (
                <Button
                  color="orange"
                  onClick={() => {
                    setDetailModalOpen(false);
                    setSelectedId(selectedInsurance.id);
                    setRejectModalOpen(true);
                  }}
                >
                  <Icon name="ban" /> Recusar Seguro
                </Button>
              )}
            </>
          )}
        </Modal.Actions>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        size="small"
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
      >
        <Modal.Header>
          <Icon name="warning sign" color="red" /> Confirmar Cancelamento
        </Modal.Header>
        <Modal.Content>
          <p>
            Tem certeza que deseja cancelar este seguro prestamista? Esta ação não pode
            ser desfeita.
          </p>
          <p>
            <strong>Atenção:</strong> Seguros cancelados não serão transmitidos nos
            próximos lotes mensais.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setCancelModalOpen(false)}>Voltar</Button>
          <Button color="red" loading={cancelling} onClick={handleCancel}>
            <Icon name="cancel" /> Confirmar Cancelamento
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Reject Modal */}
      <Modal
        size="small"
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
      >
        <Modal.Header>
          <Icon name="ban" color="red" /> Confirmar Recusa
        </Modal.Header>
        <Modal.Content>
          <p>
            Tem certeza que deseja assinalar este seguro prestamista como Recusado?
            Esta é uma ação administrativa.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setRejectModalOpen(false)}>Voltar</Button>
          <Button color="orange" loading={rejecting} onClick={handleReject}>
            <Icon name="ban" /> Confirmar Recusa
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Create Modal */}
      <CreateLenderInsuranceModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={fetchData}
        estateId={user.estate?.id}
        brokerId={user.broker?.id}
        estates={
          availableBrokersAndEstates?.estates?.map((e: any) => ({
            id: e.value,
            name: e.text
          })) || []
        }
      />
    </Container>
  );
};

export default connect(mapState)(LenderInsuranceList);
